import {
  Commission,
  CommissionProfile,
  formatCm2BucketLabel,
  isCm2PriceType,
  parseCm2BucketValue,
} from '../../../core/models/entities/Commission'
import { CommissionDto, CommissionProfileDto } from '../responses/CommissionProfileDto'

export function mapToCommissionProfile(dto: CommissionProfileDto): CommissionProfile {
  return {
    ...dto,
    overAll: dto.overAll ? mapToCommission(dto.overAll) : undefined,
    additionalFees: dto.additionalFees ? mapToCommission(dto.additionalFees) : undefined,
    heating: dto.heating ? mapToCommission(dto.heating) : undefined,
    // Null-guards: the profile is embedded in organization payloads (which in
    // turn ride along on design/logo responses) — a partial payload must not
    // throw inside react-query's select.
    typeOnly: dto.typeOnly ? dto.typeOnly.map(mapToCommission) : [],
    methods: dto.methods ? dto.methods.map(mapToMethodCommission) : [],
    createdAtUtc: new Date(dto.createdAtUtc),
    updatedAtUtc: new Date(dto.updatedAtUtc)
  }
}

export function mapToCommission(dto: CommissionDto): Commission {
  return {
    ...dto
  }
}

/**
 * Method rules need normalizing because cm2 size buckets have two shapes:
 *
 * - Correct rows (post-fix) store the numeric bucket in maxSizeCM2 with
 *   maxDimensions = null (the only shape the API price engine matches for
 *   CM2 price groups). The editors key size rows on the bucket LABEL
 *   ("100 cm²"), so the label is rebuilt for display/identity.
 * - Legacy defective rows stored the label in maxDimensions with
 *   maxSizeCM2 = null (they never matched in the price engine). The numeric
 *   value is parsed out so the editor still shows the override the user set
 *   and the live example can evaluate it; the next save then writes the row
 *   in the correct shape (see mapToCommissionProfilePayload).
 */
function mapToMethodCommission(dto: CommissionDto): Commission {
  const commission = mapToCommission(dto)
  if (commission.maxSizeCM2 != null) {
    if (
      !commission.maxDimensions ||
      parseCm2BucketValue(commission.maxDimensions) != null
    ) {
      commission.maxDimensions = formatCm2BucketLabel(commission.maxSizeCM2)
    }
  } else {
    const parsedBucket = parseCm2BucketValue(commission.maxDimensions)
    if (parsedBucket != null) {
      commission.maxSizeCM2 = parsedBucket
    }
  }
  return commission
}

/**
 * Maps the edited profile to the shape PUT organization/{id}/commission
 * expects. STANDARD rows pass through unchanged (maxDimensions "HxW").
 * cm2 size rows are written in the shape the API price engine requires:
 * the numeric bucket in maxSizeCM2, maxDimensions cleared, and a CM2
 * priceType so PriceAdjustmentService.MaxSizeIsBelowLogoSize takes the CM2
 * branch. Legacy defective rows (bucket label in maxDimensions) are healed
 * by the same mapping on their next save.
 */
export function mapToCommissionProfilePayload(
  profile: CommissionProfile
): CommissionProfile {
  return {
    ...profile,
    methods: (profile.methods ?? []).map(mapToMethodCommissionPayload),
  }
}

function mapToMethodCommissionPayload(commission: Commission): Commission {
  const cm2 =
    commission.maxSizeCM2 ?? parseCm2BucketValue(commission.maxDimensions)
  if (cm2 == null) {
    // STANDARD size rows and (engine-inert) base rows are already correct.
    return commission
  }
  return {
    ...commission,
    maxSizeCM2: cm2,
    maxDimensions: null,
    // Keep the exact NAV price group when the row carries one; otherwise any
    // CM2 group makes the engine take the CM2 branch (TYPE1/2/3 behave the
    // same in MaxSizeIsBelowLogoSize).
    priceType: isCm2PriceType(commission.priceType)
      ? commission.priceType
      : 'CM2',
  }
}
